import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useJsApiLoader, type Libraries } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

export const GOOGLE_MAPS_LOADER_ID = 'svdy-google-maps';
export const GOOGLE_MAPS_LIBRARIES = Object.freeze([
  'places',
  'marker',
]) as unknown as Libraries;

/**
 * Every Google Maps surface must use this exact option object. The upstream
 * Loader is a global singleton and synchronously throws when a later caller
 * changes even one option (id, libraries, language, region, or version).
 * Freeze both levels so callers cannot mutate the global loader contract.
 */
export const GOOGLE_MAPS_LOADER_OPTIONS = Object.freeze({
  id: GOOGLE_MAPS_LOADER_ID,
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
  libraries: GOOGLE_MAPS_LIBRARIES,
  language: 'en',
  region: 'US',
  version: 'weekly',
});

export function useSvdyGoogleMaps() {
  const loader = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const authFailed = useGoogleMapsAuthFailure();
  /**
   * AN AUTH FAILURE IS FOLDED INTO `loadError`, in ONE place (2026-08 → 2026-09-08).
   *
   * It is not a load failure — the script loads fine, so `isLoaded` stays true and `loadError` stays
   * undefined, which is precisely why five surfaces sat there showing Google's grey overlay. Every one
   * of them already branches on `loadError` to render the app's own "map unavailable" fallback, so
   * synthesizing it here fixes all five without five divergent edits, which is the drift this repo keeps
   * paying for (svdy-rules §10.5).
   *
   * `authFailed` stays separate so the distinction is still available to anything that needs it — the
   * two causes are not the same and the console diagnostic differs.
   */
  return {
    ...loader,
    authFailed,
    // `loader?.` and not `loader.`: the upstream hook's contract is an object, but a test double — and
    // any future upstream change — can hand back undefined, and crashing every map surface is a worse
    // outcome than degrading to "unavailable". Caught by an existing gate that mocks the loader.
    loadError: loader?.loadError ?? (authFailed ? new Error('gm_authFailure') : undefined),
  };
}

/**
 * GOOGLE'S AUTH FAILURE IS A GLOBAL CALLBACK, NOT AN ERROR (2026-09-08).
 *
 * When the key, referrer, billing or enabled-API set does not check out, the Maps JS API does three
 * things: it draws its OWN grey overlay with "This page can't load Google Maps correctly / Do you own
 * this website?", it logs a `…MapError` to the console, and it calls `window.gm_authFailure` if one
 * exists. It does NOT throw, so `loadError` is undefined and a React error boundary never fires.
 *
 * Until now this repo defined no handler. So the only thing the owner could see was Google's own
 * untranslated, developer-facing dialog sitting on top of a dead map, the app's own
 * `GoogleMapsUnavailableDialog` never opened, and nothing recorded WHICH of the six causes it was —
 * a surface that cannot report its own state (svdy-ui §19).
 *
 * Module-level, because the callback is global and one page can hold several maps: the first failure
 * must reach every mounted surface, and a map mounted afterwards must learn about it too.
 */
let googleMapsAuthFailed = false;
const authFailureSubscribers = new Set<(failed: boolean) => void>();

function installAuthFailureHandler(): void {
  if (typeof window === 'undefined') return;
  const w = window as typeof window & { gm_authFailure?: () => void; __svdyGmAuthHook?: boolean };
  if (w.__svdyGmAuthHook) return;
  w.__svdyGmAuthHook = true;
  const previous = w.gm_authFailure;
  w.gm_authFailure = () => {
    googleMapsAuthFailed = true;
    // The console line is the diagnostic half. Google logs the specific `…MapError` itself; this says
    // which of the checkable causes to look at, so the next report arrives with an answer rather than
    // a screenshot of a grey box.
    // eslint-disable-next-line no-console
    console.error(
      '[svdy] Google Maps authentication failed (gm_authFailure). Check, in this order: '
      + '(1) the console line above for the specific …MapError; '
      + '(2) whether this exact origin is in the key\'s HTTP-referrer allowlist — '
      + `current origin is ${window.location.origin}; `
      + '(3) that Maps JavaScript API and Places API are enabled on the key; '
      + '(4) billing on the Cloud project. '
      + 'A blocked request to gen_204?csp_test=true (browser extension / ad blocker) also lands here '
      + 'and looks identical while the key is perfectly fine.',
    );
    authFailureSubscribers.forEach((fn) => fn(true));
    previous?.();
  };
}

/** Subscribes to the global auth-failure flag. Safe to call from several surfaces at once. */
export function useGoogleMapsAuthFailure(): boolean {
  const [failed, setFailed] = React.useState(googleMapsAuthFailed);
  React.useEffect(() => {
    installAuthFailureHandler();
    // A failure that happened before this surface mounted must still be reported by it.
    if (googleMapsAuthFailed) setFailed(true);
    authFailureSubscribers.add(setFailed);
    return () => {
      authFailureSubscribers.delete(setFailed);
    };
  }, []);
  return failed;
}

/** Test seam: lets a gate drive the global flag without a real Google failure. */
export function __setGoogleMapsAuthFailedForTest(failed: boolean): void {
  googleMapsAuthFailed = failed;
  authFailureSubscribers.forEach((fn) => fn(failed));
}

interface GoogleMapsErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface GoogleMapsErrorBoundaryState {
  failed: boolean;
}

/** Keeps a Google loader/render failure local to the map leaf. */
export class GoogleMapsErrorBoundary extends Component<
  GoogleMapsErrorBoundaryProps,
  GoogleMapsErrorBoundaryState
> {
  state: GoogleMapsErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): GoogleMapsErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep diagnostic detail in the console while presenting a safe local UI.
    // eslint-disable-next-line no-console
    console.error('Google Maps surface failed', error, info.componentStack);
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export const GoogleMapsUnavailableDialog: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
}> = ({ open, title, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning">{t('common:location.mapUnavailable')}</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
